import {
  ReferralTier,
  isEligibleReferralTier,
  referralEndpointPrefix,
  referralRenewalInterval
} from 'notability-services-common-universal'
import {fetchGuestPrices, type NotabilityPrice} from './stripePrices'

export type ReferralValidation = {valid: true; tier: ReferralTier} | {valid: false}

function isValidReferralResponse(body: unknown): body is {tier: ReferralTier} {
  return (
    typeof body === 'object' &&
    body !== null &&
    'tier' in body &&
    typeof body.tier === 'string' &&
    isEligibleReferralTier(body.tier)
  )
}

/**
 * Resolves a referral code's public validity and tier from the API service via
 * `GET /referral/validate` (no auth — code existence + redemption cap only). Used for the pre-auth
 * landing; per-user eligibility is enforced later by the redemption/checkout endpoints.
 *
 * Any non-200 response or network/parse failure resolves to `{valid: false}` so callers render the
 * not-available state rather than crashing.
 *
 * `mockTier` (honored only when `localDev` is set) resolves a valid tier without a backend; a
 * present-but-invalid value resolves invalid. Local-dev-gated so it can't leak to production.
 */
export async function validateReferralCode(
  apiServiceUrl: string,
  code: string,
  opts?: {localDev?: boolean; mockTier?: string}
): Promise<ReferralValidation> {
  if (opts?.localDev && opts.mockTier !== undefined) {
    return isEligibleReferralTier(opts.mockTier) ? {valid: true, tier: opts.mockTier} : {valid: false}
  }

  try {
    const res = await fetch(`${apiServiceUrl}${referralEndpointPrefix}/validate?code=${encodeURIComponent(code)}`, {
      method: 'GET',
      headers: {'Content-Type': 'application/json'}
    })

    if (!res.ok) {
      return {valid: false}
    }

    const body: unknown = await res.json()
    return isValidReferralResponse(body) ? {valid: true, tier: body.tier} : {valid: false}
  } catch (error) {
    console.error('Failed to validate referral code:', error)
    return {valid: false}
  }
}

/**
 * The published monthly price for a referral recipient's tier — the same price the referral checkout
 * bills against — sourced from the public (cached, no-auth) consumer price set so the free-trial
 * disclaimer can render server-side without a checkout session. Returns null for a tier-less step or
 * when the price set can't be fetched/matched; the disclaimer then falls back to its price-less
 * variant rather than blocking the page.
 *
 * The interval matches the referral checkout lookup key built in `referral_endpoint.ts`; both derive
 * from `referralRenewalPeriod`, so the displayed renewal price always reflects what redemption bills.
 */
export async function fetchReferralRenewalPrice(
  apiServiceUrl: string,
  tier: ReferralTier | null
): Promise<NotabilityPrice | null> {
  if (!tier) {
    return null
  }
  try {
    const {prices} = await fetchGuestPrices(apiServiceUrl)
    return prices.find((price) => price.tier === tier && price.intervalUnit === referralRenewalInterval) ?? null
  } catch (error) {
    console.error('Failed to fetch referral renewal price:', error)
    return null
  }
}
